import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { LoaderFacade } from '../../state/loader/facades/loader.facade';

@Component({
  selector: 'app-global-loader',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div
      *ngIf="isLoading$ | async"
      class="fixed inset-0 bg-base-100 bg-opacity-60 flex items-center justify-center z-[9999]"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 275.8 269.7"
        role="status"
        aria-label="Loading"
        class="h-14 w-14 opacity-50"
      >
        <style>
          :root {
            --d: 1.5s;
            --start: calc(var(--d) * -0.35);
          }

          /* center reveal: ring at rest, grows to draw spokes, shrinks back */
          .revealC {
            animation: reveal var(--d) ease-in-out infinite;
            animation-delay: var(--start);
          }
          @keyframes reveal {
            0%,
            6.7% {
              r: 54px;
            }
            23.3%,
            66.7% {
              r: 145px;
            }
            81.3%,
            100% {
              r: 54px;
            }
          }

          /* petal masks: thick stroke drawn end-to-end along each centerline */
          .pmask {
            fill: none;
            stroke: #fff;
            stroke-width: 26;
            stroke-linecap: round;
            stroke-linejoin: round;
            stroke-dasharray: 120;
            stroke-dashoffset: 0;
            animation: pdraw var(--d) ease-in-out infinite;
            animation-delay: var(--start);
          }
          @keyframes pdraw {
            0%,
            26.7% {
              stroke-dashoffset: 120;
            }
            44%,
            81.3% {
              stroke-dashoffset: 0;
            }
            97.3%,
            100% {
              stroke-dashoffset: 120;
            }
          }

          .petal {
            animation: pfade var(--d) ease-in-out infinite;
            animation-delay: var(--start);
          }
          @keyframes pfade {
            0%,
            26.7% {
              opacity: 0;
            }
            27.5%,
            96.5% {
              opacity: 1;
            }
            97.3%,
            100% {
              opacity: 0;
            }
          }

          /* rotate the assembled logo 60deg (one symmetry step) */
          .spin {
            animation: spin var(--d) ease-in-out infinite;
            animation-delay: var(--start);
            transform-box: view-box;
            transform-origin: 137.9px 134.85px;
          }
          @keyframes spin {
            0%,
            36.7% {
              transform: rotate(0deg);
            }
            63.3%,
            100% {
              transform: rotate(60deg);
            }
          }

          /* reduced motion: hold the full logo, no animation */
          @media (prefers-reduced-motion: reduce) {
            .revealC,
            .pmask,
            .petal,
            .spin {
              animation: none;
            }
            .revealC {
              r: 145px;
            }
          }
        </style>

        <defs>
          <mask id="reveal">
            <circle class="revealC" cx="137.9" cy="134.85" r="54" fill="#fff" />
          </mask>
          <mask id="pm0">
            <path
              class="pmask"
              d="M104.35,3.5 L120.9,35.75 Q137.9,56.5 154.9,35.75 L171.45,3.5"
              transform="rotate(0 137.9 134.85)"
            />
          </mask>
          <mask id="pm1">
            <path
              class="pmask"
              d="M104.35,3.5 L120.9,35.75 Q137.9,56.5 154.9,35.75 L171.45,3.5"
              transform="rotate(-60 137.9 134.85)"
            />
          </mask>
          <mask id="pm2">
            <path
              class="pmask"
              d="M104.35,3.5 L120.9,35.75 Q137.9,56.5 154.9,35.75 L171.45,3.5"
              transform="rotate(-120 137.9 134.85)"
            />
          </mask>
          <mask id="pm3">
            <path
              class="pmask"
              d="M104.35,3.5 L120.9,35.75 Q137.9,56.5 154.9,35.75 L171.45,3.5"
              transform="rotate(180 137.9 134.85)"
            />
          </mask>
          <mask id="pm4">
            <path
              class="pmask"
              d="M104.35,3.5 L120.9,35.75 Q137.9,56.5 154.9,35.75 L171.45,3.5"
              transform="rotate(120 137.9 134.85)"
            />
          </mask>
          <mask id="pm5">
            <path
              class="pmask"
              d="M104.35,3.5 L120.9,35.75 Q137.9,56.5 154.9,35.75 L171.45,3.5"
              transform="rotate(60 137.9 134.85)"
            />
          </mask>
        </defs>

        <g class="spin" fill="#54be99">
          <!-- center ring + radiating spokes (exact art), revealed from the middle out -->
          <path
            mask="url(#reveal)"
            d="M190.6,145.7c.4-2.1,2.3-3.6,4.5-3.6h77.7c1.6,0,2.9-1.3,2.9-2.8,0-1.5,0-3,0-4.5s0-3,0-4.5-1.3-2.8-2.9-2.8h-77.7c-2.2,0-4-1.5-4.5-3.6-2.3-11.3-8.3-21.6-16.9-29.3-1.6-1.4-1.9-3.8-.9-5.7l38.8-67.3c.8-1.4.4-3.1-1-3.9-2.6-1.6-5.2-3.1-7.8-4.5s-3.1-.2-3.9,1.1l-38.8,67.3c-1.1,1.9-3.3,2.8-5.4,2.1-5.4-1.8-11.1-2.7-16.9-2.7s-11.5.9-16.9,2.7c-2.1.7-4.3-.2-5.4-2.1L76.8,14.3c-.8-1.4-2.5-1.9-3.9-1.1-2.7,1.4-5.3,2.9-7.8,4.5s-1.7,2.6-1,3.9l38.9,67.3c1.1,1.9.7,4.2-.9,5.7-8.6,7.7-14.6,18-16.9,29.3-.4,2.1-2.3,3.6-4.5,3.6H3c-1.6,0-2.9,1.3-2.9,2.8,0,1.5,0,3,0,4.5s0,3,0,4.5,1.3,2.8,2.9,2.8h77.7c2.2,0,4,1.5,4.5,3.6,2.3,11.3,8.3,21.6,16.9,29.3,1.6,1.4,1.9,3.8.9,5.7l-38.9,67.3c-.8,1.4-.4,3.1,1,3.9,2.6,1.6,5.2,3.1,7.8,4.5s3.1.2,3.9-1.1l38.8-67.3c1.1-1.9,3.3-2.8,5.4-2.1,5.4,1.8,11.1,2.7,16.9,2.7s11.5-.9,16.9-2.7c2.1-.7,4.3.2,5.4,2.1l38.8,67.3c.8,1.4,2.5,1.9,3.9,1.1,2.7-1.4,5.3-2.9,7.8-4.5,1.3-.8,1.7-2.6,1-3.9l-38.8-67.3c-1.1-1.9-.7-4.3.9-5.7,8.6-7.7,14.6-18,16.9-29.3ZM157.5,168.8c-11.7,6.8-27.3,6.9-39.2,0-6-3.5-11-8.5-14.4-14.4-3.4-5.9-5.3-12.6-5.3-19.6,0-14,7.5-27,19.7-34,5.9-3.4,12.7-5.2,19.6-5.2s13.7,1.8,19.6,5.2h0c6.1,3.5,11,8.5,14.4,14.4,3.4,5.9,5.3,12.6,5.3,19.6,0,14-7.5,27-19.7,34Z"
          />

          <!-- petals (exact art), each drawn end-to-end through its mask -->
          <path
            class="petal"
            mask="url(#pm0)"
            d="M177.4,2.7c1.2.4,1.8,1.8,1.2,2.9l-17.2,33.5c-4.6,8.8-13.6,14.4-23.5,14.4s-18.9-5.5-23.5-14.4L97.2,5.6c-.6-1.1,0-2.6,1.2-2.9,3.4-1,6.8-1.9,10.3-2.6s2.3.3,2.8,1.3l15.9,31c2.1,4,6,6.4,10.5,6.4s8.4-2.4,10.5-6.4l15.9-31c.5-1,1.7-1.6,2.8-1.3,3.5.7,6.9,1.6,10.3,2.6Z"
          />
          <path
            class="petal"
            mask="url(#pm1)"
            d="M67.4,94.1c-4.8,8.2-13.4,13.2-22.9,13.2s-.9,0-1.3,0l-37.6-1.8c-1.3,0-2.2-1.3-1.9-2.5.8-3.4,1.8-6.9,2.8-10.2s1.4-1.8,2.6-1.8l34.8,1.7c4.5.3,8.5-2,10.8-5.9,2.2-3.9,2.1-8.5-.3-12.3l-18.9-29.3c-.6-1-.5-2.3.2-3.1,2.4-2.6,4.8-5.1,7.4-7.6.9-.9,2.5-.7,3.1.4l20.4,31.6c5.4,8.4,5.6,18.9.7,27.5Z"
          />
          <path
            class="petal"
            mask="url(#pm2)"
            d="M66.7,203l-20.4,31.6c-.7,1.1-2.2,1.3-3.1.4-2.6-2.4-5-4.9-7.4-7.6-.8-.9-.9-2.1-.2-3.1l18.9-29.3c2.4-3.8,2.5-8.4.3-12.3-2.3-3.9-6.3-6.1-10.8-5.9l-34.8,1.7c-1.2,0-2.2-.7-2.6-1.8-1.1-3.4-2-6.8-2.8-10.2-.3-1.3.6-2.5,1.9-2.5l37.6-1.8c9.9-.5,19.2,4.6,24.2,13.2,5,8.6,4.7,19.2-.7,27.5Z"
          />
          <path
            class="petal"
            mask="url(#pm3)"
            d="M177.4,267c-3.4,1-6.8,1.9-10.3,2.6-1.1.2-2.3-.3-2.8-1.3l-15.9-31c-2.1-4-6-6.4-10.5-6.4s-8.4,2.4-10.5,6.4l-15.9,31c-.5,1-1.7,1.6-2.8,1.3-3.5-.7-6.9-1.6-10.3-2.6-1.2-.4-1.8-1.8-1.2-2.9l17.2-33.5c4.6-8.8,13.6-14.4,23.5-14.4s19,5.5,23.5,14.4l17.2,33.5c.6,1.1,0,2.6-1.2,2.9Z"
          />
          <path
            class="petal"
            mask="url(#pm4)"
            d="M221.1,182.8c-2.2,3.9-2.1,8.5.3,12.3l18.9,29.3c.6,1,.5,2.2-.2,3.1-2.4,2.6-4.8,5.1-7.4,7.6-.9.9-2.5.7-3.1-.4l-20.4-31.6c-5.4-8.4-5.6-18.9-.7-27.5,5-8.6,14.2-13.6,24.2-13.2l37.6,1.8c1.3,0,2.2,1.3,1.9,2.5-.8,3.4-1.8,6.8-2.8,10.2-.4,1.1-1.4,1.8-2.6,1.8l-34.8-1.7c-4.5-.2-8.5,2-10.8,5.9Z"
          />
          <path
            class="petal"
            mask="url(#pm5)"
            d="M272.1,102.9c.3,1.3-.6,2.5-1.9,2.5l-37.6,1.8c-.4,0-.9,0-1.3,0-9.4,0-18.1-5-22.9-13.2-5-8.6-4.7-19.2.7-27.5l20.4-31.6c.7-1.1,2.2-1.3,3.1-.4,2.6,2.4,5,5,7.4,7.6.8.9.9,2.1.2,3.1l-18.9,29.3c-2.4,3.8-2.5,8.4-.3,12.3,2.3,3.9,6.3,6.1,10.8,5.9l34.8-1.7c1.2,0,2.2.7,2.6,1.8,1.1,3.4,2,6.8,2.8,10.2Z"
          />
        </g>
      </svg>
    </div>
  `,
})
export class GlobalLoaderComponent {
  private readonly loaderFacade = inject(LoaderFacade);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  isLoading$ = this.loaderFacade.isLoading$;

  constructor() {
    // Safety net: the overlay is a request counter, so a single request that
    // never settles (e.g. a backend call hanging on a slow/unavailable Vertex
    // Workbench) would pin the full-screen overlay indefinitely. Zero the
    // counter whenever a navigation completes so a stuck overlay can never
    // survive a route change and hold the whole app hostage.
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.loaderFacade.resetLoader());
  }
}
